import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsClockwise, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Endpoint catalog powering the "Try it" playground sidebar.
 * Keys correspond to entries called via openTry(key) from doc sections.
 */
export const TRY_ENDPOINTS = {
  payouts: {
    title: "GET /v1/payouts",
    path: "/v1/payouts",
    params: [
      { key: "brand", label: "Brand", type: "text", placeholder: "Optional — e.g. MacPaw" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
    ],
  },
  commissions: {
    title: "GET /v1/reports/commissions",
    path: "/v1/reports/commissions",
    params: [
      { key: "brand", label: "Brand", type: "text", placeholder: "Optional — all brands if blank" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
    ],
  },
  clicks: {
    title: "GET /v1/reports/clicks",
    path: "/v1/reports/clicks",
    params: [
      { key: "brand", label: "Brand", type: "text", placeholder: "Optional — all brands if blank" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
    ],
  },
  brands: {
    title: "GET /v1/brands",
    path: "/v1/brands",
    params: [],
  },
  clicksLog: {
    title: "GET /v1/reports/clicks-log",
    path: "/v1/reports/clicks-log",
    params: [
      { key: "brand", label: "Brand", type: "text", placeholder: "Optional" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
    ],
  },
  conversions: {
    title: "GET /v1/reports/conversions",
    path: "/v1/reports/conversions",
    params: [
      { key: "brand", label: "Brand", type: "text", placeholder: "Optional — all brands if blank" },
      { key: "start_date", label: "Start date", type: "date" },
      { key: "end_date", label: "End date", type: "date" },
      { key: "sub_id2", label: "click_id (sub_id2)", type: "text", placeholder: "Match your stored click_id" },
      { key: "sub_id3", label: "sub_id3", type: "text", placeholder: "Optional" },
      { key: "status", label: "Status", type: "text", placeholder: "APPROVED / PENDING / REJECTED" },
    ],
  },
};

/**
 * Right-side slide-in API playground. Lets the publisher exercise any
 * endpoint with their actual key + parameters and inspect raw response.
 * State persists in localStorage so a reload restores where they were.
 */
export function TryItPanel({ endpointKey, endpoint, apiKey, restoredValues, autoFireNonce, onClose }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState(null);
  const [elapsed, setElapsed] = useState(null);

  // Reset or restore state on endpoint change
  useEffect(() => {
    if (endpoint) {
      setValues(restoredValues || {});
      setResponse(null);
      setStatus(null);
      setElapsed(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpointKey]);

  // Persist session so reload restores where the pub left off
  useEffect(() => {
    if (!endpointKey) return;
    try {
      localStorage.setItem(
        "redclub:api-playground:v1",
        JSON.stringify({ endpointKey, values }),
      );
    } catch {}
  }, [endpointKey, values]);

  const url = useMemo(() => {
    if (!endpoint) return "";
    const qs = new URLSearchParams();
    for (const p of endpoint.params) {
      const v = (values[p.key] || "").trim();
      if (v) qs.set(p.key, v);
    }
    const query = qs.toString();
    return `${BASE_URL}/api${endpoint.path}${query ? `?${query}` : ""}`;
  }, [endpoint, values]);

  const send = async () => {
    if (!apiKey) {
      toast.error("Generate an API key first (Key Management tab)");
      return;
    }
    setLoading(true);
    setResponse(null);
    setStatus(null);
    const t0 = performance.now();
    try {
      const res = await fetch(url, { headers: { "X-API-Key": apiKey } });
      setStatus(res.status);
      setElapsed(Math.round(performance.now() - t0));
      let body;
      try { body = await res.json(); } catch { body = await res.text(); }
      setResponse(body);
    } catch (e) {
      setStatus("ERR");
      setResponse({ error: String(e) });
      setElapsed(Math.round(performance.now() - t0));
    } finally {
      setLoading(false);
    }
  };

  // Auto-fire when restored from localStorage (once per restore event)
  useEffect(() => {
    if (!autoFireNonce || !endpoint || !apiKey) return;
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFireNonce, apiKey]);

  const open = !!endpoint;
  const statusTone =
    status === null ? "bg-slate-100 text-slate-500"
    : status >= 200 && status < 300 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : status === 401 || status === 403 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-red-50 text-red-700 border-red-200";

  return (
    <aside
      className={`fixed top-0 right-0 h-screen w-full sm:w-[440px] bg-white border-l border-slate-200 shadow-xl z-40 transform transition-transform duration-200 ease-out ${
        open ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`}
      data-testid="try-it-panel"
      aria-hidden={!open}
    >
      {endpoint && (
        <div className="h-full flex flex-col">
          <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">
                API Playground
              </p>
              <h3 className="font-mono text-[13px] font-semibold text-slate-900 mt-0.5">
                {endpoint.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-900 text-xl leading-none px-1"
              data-testid="try-it-close"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {endpoint.params.length > 0 && (
              <div className="space-y-3 mb-4">
                {endpoint.params.map((p) => (
                  <div key={p.key}>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                      {p.label}
                    </Label>
                    <Input
                      type={p.type === "date" ? "date" : "text"}
                      placeholder={p.placeholder || ""}
                      value={values[p.key] || ""}
                      onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
                      data-testid={`try-input-${p.key}`}
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="mb-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                Request URL
              </Label>
              <div className="bg-slate-900 text-slate-100 rounded-md px-3 py-2 text-[11px] font-mono break-all leading-snug">
                {url}
              </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <Button
                onClick={send}
                disabled={loading || !apiKey}
                className="bg-[#002FA7] hover:bg-[#00227A] text-white h-9"
                data-testid="try-send-btn"
              >
                {loading ? <ArrowsClockwise size={14} className="mr-1.5 animate-spin" /> : <Play size={12} weight="fill" className="mr-1.5" />}
                Send
              </Button>
              {status !== null && (
                <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded text-[11px] font-mono border ${statusTone}`}>
                  <span className="font-bold">{status}</span>
                  <span className="opacity-60">·</span>
                  <span>{elapsed}ms</span>
                </div>
              )}
            </div>

            {!apiKey && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
                Generate an API key first to send requests.
              </div>
            )}

            {response !== null && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 block">
                  Response
                </Label>
                <pre className="bg-slate-900 text-slate-100 rounded-md px-3 py-2.5 text-[11px] font-mono overflow-x-auto leading-snug">
                  {typeof response === "string" ? response : JSON.stringify(response, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
